/**
 * MoreMenu — overflow menu for the Action Panel.
 *
 * Desktop: Radix DropdownMenu popping up from the ⋮ button.
 * Mobile: Vaul Drawer (bottom sheet) with tappable action rows.
 *
 * Uses shared task-actions registry + executors so that all surfaces
 * (Action Panel, context menus, keyboard shortcuts) share one source of truth.
 */

import { memo, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import Ellipsis from "@/shared/icons/pikaicons/stroke/general/three-dots-menu-horizontal.svg?react"
import { cn } from "@/shared/utils/general/cn"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { useActiveTask } from "@/features/task-detail/hooks/useActiveTask"
import { TASK_ACTIONS, useTaskActionExecutors } from "@/features/task-actions"
import { shortcutLabel } from "@/shared/utils/platform/shortcutLabel"
import { UserStatusPicker } from "@/shared/components/UserStatusPicker"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/app/components/ui/dropdown-menu"
import { Drawer, DrawerContent, DrawerClose } from "@/app/components/ui/drawer"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import { db, ObjectStatus } from "@/db/schema"
import { startTimer, stopTimer } from "@/db/operations"
import { useLiveQueryImmediate } from "@/db/hooks"
import { useViewContext } from "@/app/stores/viewContext"
import { RecurrenceSheet } from "@/features/recurrence"
import { useRecurrence } from "@/features/recurrence"
import RepeatIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/repeat-square.svg?react"
import TimerIcon from "@/shared/icons/pikaicons/stroke/time/timer-default.svg?react"
import TimerOffIcon from "@/shared/icons/pikaicons/stroke/time/timer-off.svg?react"
import type { ViewId } from "@/features/views/viewRegistry"
import type { ActionPanelContext } from "./useActionPanelState"

const HEADLINE_VIEWS = new Set<ViewId>(["project", "list"])

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MoreMenuProps {
  /** Current action panel context */
  context: ActionPanelContext
  /** Task IDs to operate on */
  taskIds: string[]
}

// ---------------------------------------------------------------------------
// Desktop: DropdownMenu
// ---------------------------------------------------------------------------

function DesktopMoreMenu({ context, taskIds }: MoreMenuProps) {
  const { t } = useTranslation()
  const { closeTask } = useActiveTask()
  const exec = useTaskActionExecutors(taskIds, { closeTask })
  const [showRecurrenceSheet, setShowRecurrenceSheet] = useState(false)

  const viewId = useViewContext((s) => s.viewId)
  const canConvertToSection = HEADLINE_VIEWS.has(viewId)

  // Load task object for single-task features (recurrence, timer)
  const singleTaskId = context === "task-open" && taskIds.length === 1 ? taskIds[0] : undefined
  const [task] = useLiveQueryImmediate(() => singleTaskId ? db.totodo.get(singleTaskId) : undefined, [singleTaskId], undefined)
  const recurrence = useRecurrence(task?.isRecurringTemplate ? singleTaskId : undefined)
  const hasRecurrence = !!recurrence || !!task?.recurringTemplateId
  const isTimerRunning = !!task?.timerStartedAt

  if (context === "no-selection") return null

  const A = TASK_ACTIONS
  // Aliases for bracket-notation keys (JSX can't use A["set-status"].icon)
  const setStatus = A["set-status"]
  const copyToClip = A["copy-to-clipboard"]
  const copyLink = A["copy-link"]
  const toSection = A["convert-to-section"]
  const toProject = A["convert-to-project"]

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center justify-center rounded-lg p-2 transition-colors duration-150",
            "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "min-h-[40px] min-w-[40px] text-foreground/70 hover:bg-primary/10 hover:text-foreground cursor-pointer",
          )}
          aria-label={t("More actions")}
        >
          <Ellipsis className="size-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="end"
        className="min-w-[200px]"
        data-action-panel
      >
        {context === "tasks-selected" && (
          <>
            {/* Status actions */}
            <DropdownMenuItem onClick={exec.complete}>
              <A.complete.icon className="size-4" />
              {t(A.complete.labelBulk!)}
              <DropdownMenuShortcut>
                {shortcutLabel(A.complete.shortcut!)}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exec.cancel}>
              <A.cancel.icon className="size-4" />
              {t(A.cancel.labelBulk!)}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Schedule shortcuts */}
            <DropdownMenuItem onClick={exec.schedule}>
              <A.schedule.icon className="size-4" />
              {t(A.schedule.label)}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exec.evening}>
              <A.evening.icon className="size-4" />
              {t(A.evening.label)}
              <DropdownMenuShortcut>
                {shortcutLabel(A.evening.shortcut!)}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exec.someday}>
              <A.someday.icon className="size-4" />
              {t(A.someday.label)}
              <DropdownMenuShortcut>
                {shortcutLabel(A.someday.shortcut!)}
              </DropdownMenuShortcut>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Deadline + Set Status + Tags */}
            <DropdownMenuItem onClick={exec.deadline}>
              <A.deadline.icon className="size-4" />
              {t(A.deadline.label)}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => useViewContext.getState().openTagPicker(taskIds)}>
              <A.tags.icon className="size-4" />
              {t(A.tags.label)}
              <DropdownMenuShortcut>
                {shortcutLabel(A.tags.shortcut!)}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <setStatus.icon className="size-4" />
                {t(setStatus.label)}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="p-1" data-action-panel>
                <UserStatusPicker
                  taskId={taskIds}
                  onClose={() => {
                    // DropdownMenu auto-closes via Radix portal dismiss
                  }}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSeparator />

            {/* Organize */}
            <DropdownMenuItem onClick={exec.duplicate}>
              <A.duplicate.icon className="size-4" />
              {t(A.duplicate.labelBulk!)}
              <DropdownMenuShortcut>
                {shortcutLabel(A.duplicate.shortcut!)}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exec.copyToClipboard}>
              <copyToClip.icon className="size-4" />
              {t(copyToClip.label)}
            </DropdownMenuItem>
          </>
        )}

        {context === "task-open" && (
          <>
            {/* Schedule */}
            <DropdownMenuItem onClick={exec.schedule}>
              <A.schedule.icon className="size-4" />
              {t(A.schedule.label)}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exec.evening}>
              <A.evening.icon className="size-4" />
              {t(A.evening.label)}
              <DropdownMenuShortcut>
                {shortcutLabel(A.evening.shortcut!)}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exec.someday}>
              <A.someday.icon className="size-4" />
              {t(A.someday.label)}
              <DropdownMenuShortcut>
                {shortcutLabel(A.someday.shortcut!)}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exec.deadline}>
              <A.deadline.icon className="size-4" />
              {t(A.deadline.label)}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => useViewContext.getState().openTagPicker(taskIds)}>
              <A.tags.icon className="size-4" />
              {t(A.tags.label)}
              <DropdownMenuShortcut>
                {shortcutLabel(A.tags.shortcut!)}
              </DropdownMenuShortcut>
            </DropdownMenuItem>

            {/* Repeat */}
            {!hasRecurrence && (
              <DropdownMenuItem onClick={() => setShowRecurrenceSheet(true)}>
                <RepeatIcon className="size-4" />
                {t("recurrence.title", "Repeat")}
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            {/* Set Status */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <setStatus.icon className="size-4" />
                {t(setStatus.label)}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="p-1" data-action-panel>
                <UserStatusPicker
                  taskId={taskIds}
                  onClose={() => {
                    // DropdownMenu auto-closes via Radix portal dismiss
                  }}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            {/* Focus & Pin */}
            <DropdownMenuItem onClick={exec.focus}>
              <A.focus.icon className="size-4" />
              {t(A.focus.label)}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exec.pin}>
              <A.pin.icon className="size-4" />
              {t(A.pin.label)}
            </DropdownMenuItem>

            {/* Timer */}
            {singleTaskId && (
              <DropdownMenuItem onClick={() => {
                if (isTimerRunning) void stopTimer(singleTaskId)
                else void startTimer(singleTaskId)
              }}>
                {isTimerRunning ? <TimerOffIcon className="size-4" /> : <TimerIcon className="size-4" />}
                {isTimerRunning ? t("Stop Timer") : t("Start Timer")}
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            {/* Organize */}
            <DropdownMenuItem onClick={exec.duplicate}>
              <A.duplicate.icon className="size-4" />
              {t(A.duplicate.label)}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exec.copyToClipboard}>
              <copyToClip.icon className="size-4" />
              {t(copyToClip.label)}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Convert */}
            <DropdownMenuItem onClick={exec.convertToSection} disabled={!canConvertToSection}>
              <toSection.icon className="size-4" />
              {t(toSection.label)}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exec.convertToProject}>
              <DynamicIcon
                type="project"
                percentage={33}
                unfinished={0}
                status={ObjectStatus.Unfinished}
                color="currentColor"
                size="xs"
              />
              {t(toProject.label)}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Share / Copy Link */}
            <DropdownMenuItem onClick={exec.copyLink}>
              <copyLink.icon className="size-4" />
              {t(copyLink.label)}
              {copyLink.shortcut && (
                <DropdownMenuShortcut>
                  {shortcutLabel(copyLink.shortcut)}
                </DropdownMenuShortcut>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void exec.share()}>
              {typeof navigator.share === "function" ? (
                <A.share.icon className="size-4" />
              ) : (
                <copyToClip.icon className="size-4" />
              )}
              {typeof navigator.share === "function"
                ? t(A.share.label)
                : t(copyToClip.label)}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>

    {/* Recurrence sheet — rendered outside DropdownMenu so it persists after menu closes */}
    {task && showRecurrenceSheet && (
      <RecurrenceSheet
        open={showRecurrenceSheet}
        onOpenChange={setShowRecurrenceSheet}
        task={task}
      />
    )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Mobile: Vaul Drawer (bottom sheet)
// ---------------------------------------------------------------------------

interface DrawerItemProps {
  icon: React.ReactNode
  label: string
  shortcut?: string
  destructive?: boolean
  disabled?: boolean
  onClick: () => void
}

function DrawerItem({
  icon,
  label,
  shortcut,
  destructive,
  disabled,
  onClick,
}: DrawerItemProps) {
  return (
    <DrawerClose asChild>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg px-4 py-3 text-base transition-colors",
          "active:bg-primary/10",
          destructive && "text-destructive",
          disabled && "opacity-40 pointer-events-none",
        )}
      >
        <span className="[&_svg]:size-5">{icon}</span>
        <span className="flex-1 text-left">{label}</span>
        {shortcut && <span className="text-xs opacity-50">{shortcut}</span>}
      </button>
    </DrawerClose>
  )
}

function DrawerSeparator() {
  return <div className="my-1 mx-4 h-px bg-foreground/5" />
}

function MobileMoreMenu({ context, taskIds }: MoreMenuProps) {
  const { t } = useTranslation()
  const { closeTask } = useActiveTask()
  const exec = useTaskActionExecutors(taskIds, { closeTask })
  const [open, setOpen] = useState(false)
  const [showStatusPicker, setShowStatusPicker] = useState(false)
  const [showRecurrenceSheet, setShowRecurrenceSheet] = useState(false)
  const viewId = useViewContext((s) => s.viewId)
  const canConvertToSection = HEADLINE_VIEWS.has(viewId)

  // Load task object for single-task features (recurrence, timer)
  const singleTaskId = context === "task-open" && taskIds.length === 1 ? taskIds[0] : undefined
  const [task] = useLiveQueryImmediate(() => singleTaskId ? db.totodo.get(singleTaskId) : undefined, [singleTaskId], undefined)
  const recurrence = useRecurrence(task?.isRecurringTemplate ? singleTaskId : undefined)
  const hasRecurrence = !!recurrence || !!task?.recurringTemplateId
  const isTimerRunning = !!task?.timerStartedAt

  const handleStatusPickerClose = useCallback(() => {
    setShowStatusPicker(false)
    setOpen(false)
  }, [])

  if (context === "no-selection") return null

  const A = TASK_ACTIONS
  const setStatus = A["set-status"]
  const copyToClip = A["copy-to-clipboard"]
  const copyLink = A["copy-link"]
  const toSection = A["convert-to-section"]
  const toProject = A["convert-to-project"]

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg px-2 py-2 transition-colors duration-150",
          "text-foreground/70 active:bg-primary/10",
        )}
        aria-label={t("More actions")}
      >
        <Ellipsis className="size-5" />
      </button>

      <Drawer
        open={open}
        onOpenChange={(v) => {
          setOpen(v)
          if (!v) setShowStatusPicker(false)
        }}
      >
        <DrawerContent data-action-panel>
          <div className="px-2 py-3">
            {showStatusPicker ? (
              /* Status picker step — replaces main menu inline */
              <div className="px-2 pb-2">
                <UserStatusPicker
                  taskId={taskIds}
                  onClose={handleStatusPickerClose}
                />
              </div>
            ) : (
              /* Main menu */
              <>
                {context === "tasks-selected" && (
                  <>
                    <DrawerItem
                      icon={<A.complete.icon />}
                      label={t(A.complete.labelBulk!)}
                      shortcut={shortcutLabel(A.complete.shortcut!)}
                      onClick={exec.complete}
                    />
                    <DrawerItem
                      icon={<A.cancel.icon />}
                      label={t(A.cancel.labelBulk!)}
                      onClick={exec.cancel}
                    />
                    <DrawerSeparator />
                    <DrawerItem
                      icon={<A.schedule.icon />}
                      label={t(A.schedule.label)}
                      onClick={exec.schedule}
                    />
                    <DrawerItem
                      icon={<A.evening.icon />}
                      label={t(A.evening.label)}
                      shortcut={shortcutLabel(A.evening.shortcut!)}
                      onClick={exec.evening}
                    />
                    <DrawerItem
                      icon={<A.someday.icon />}
                      label={t(A.someday.label)}
                      shortcut={shortcutLabel(A.someday.shortcut!)}
                      onClick={exec.someday}
                    />
                    <DrawerSeparator />
                    <DrawerItem
                      icon={<A.deadline.icon />}
                      label={t(A.deadline.label)}
                      onClick={exec.deadline}
                    />
                    <DrawerItem
                      icon={<A.tags.icon />}
                      label={t(A.tags.label)}
                      onClick={() => useViewContext.getState().openTagPicker(taskIds)}
                    />
                    {/* Set Status — opens inline picker instead of closing drawer */}
                    <button
                      type="button"
                      onClick={() => setShowStatusPicker(true)}
                      className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-base transition-colors active:bg-primary/10"
                    >
                      <span className="[&_svg]:size-5">
                        <setStatus.icon />
                      </span>
                      <span className="flex-1 text-left">
                        {t(setStatus.label)}
                      </span>
                    </button>
                    <DrawerSeparator />
                    <DrawerItem
                      icon={<A.duplicate.icon />}
                      label={t(A.duplicate.labelBulk!)}
                      shortcut={shortcutLabel(A.duplicate.shortcut!)}
                      onClick={exec.duplicate}
                    />
                    <DrawerItem
                      icon={<copyToClip.icon />}
                      label={t(copyToClip.label)}
                      onClick={exec.copyToClipboard}
                    />
                  </>
                )}
                {context === "task-open" && (
                  <>
                    {/* Schedule */}
                    <DrawerItem
                      icon={<A.schedule.icon />}
                      label={t(A.schedule.label)}
                      onClick={exec.schedule}
                    />
                    <DrawerItem
                      icon={<A.evening.icon />}
                      label={t(A.evening.label)}
                      onClick={exec.evening}
                    />
                    <DrawerItem
                      icon={<A.someday.icon />}
                      label={t(A.someday.label)}
                      onClick={exec.someday}
                    />
                    <DrawerItem
                      icon={<A.deadline.icon />}
                      label={t(A.deadline.label)}
                      onClick={exec.deadline}
                    />
                    <DrawerItem
                      icon={<A.tags.icon />}
                      label={t(A.tags.label)}
                      onClick={() => useViewContext.getState().openTagPicker(taskIds)}
                    />

                    {/* Repeat */}
                    {!hasRecurrence && (
                      <DrawerItem
                        icon={<RepeatIcon />}
                        label={t("recurrence.title", "Repeat")}
                        onClick={() => setShowRecurrenceSheet(true)}
                      />
                    )}

                    <DrawerSeparator />

                    {/* Set Status — opens inline picker instead of closing drawer */}
                    <button
                      type="button"
                      onClick={() => setShowStatusPicker(true)}
                      className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-base transition-colors active:bg-primary/10"
                    >
                      <span className="[&_svg]:size-5">
                        <setStatus.icon />
                      </span>
                      <span className="flex-1 text-left">
                        {t(setStatus.label)}
                      </span>
                    </button>

                    {/* Focus & Pin */}
                    <DrawerItem
                      icon={<A.focus.icon />}
                      label={t(A.focus.label)}
                      onClick={exec.focus}
                    />
                    <DrawerItem
                      icon={<A.pin.icon />}
                      label={t(A.pin.label)}
                      onClick={exec.pin}
                    />

                    {/* Timer */}
                    {singleTaskId && (
                      <DrawerItem
                        icon={isTimerRunning ? <TimerOffIcon /> : <TimerIcon />}
                        label={isTimerRunning ? t("Stop Timer") : t("Start Timer")}
                        onClick={() => {
                          if (isTimerRunning) void stopTimer(singleTaskId)
                          else void startTimer(singleTaskId)
                        }}
                      />
                    )}

                    <DrawerSeparator />

                    {/* Organize */}
                    <DrawerItem
                      icon={<A.duplicate.icon />}
                      label={t(A.duplicate.label)}
                      onClick={exec.duplicate}
                    />
                    <DrawerItem
                      icon={<copyToClip.icon />}
                      label={t(copyToClip.label)}
                      onClick={exec.copyToClipboard}
                    />
                    <DrawerSeparator />
                    <DrawerItem
                      icon={<toSection.icon />}
                      label={t(toSection.label)}
                      onClick={exec.convertToSection}
                      disabled={!canConvertToSection}
                    />
                    <DrawerItem
                      icon={
                        <DynamicIcon
                          type="project"
                          percentage={33}
                          unfinished={0}
                          status={ObjectStatus.Unfinished}
                          color="currentColor"
                          size="small"
                        />
                      }
                      label={t(toProject.label)}
                      onClick={exec.convertToProject}
                    />
                    <DrawerSeparator />
                    <DrawerItem
                      icon={<copyLink.icon />}
                      label={t(copyLink.label)}
                      onClick={exec.copyLink}
                    />
                    <DrawerItem
                      icon={
                        typeof navigator.share === "function"
                          ? <A.share.icon />
                          : <copyToClip.icon />
                      }
                      label={
                        typeof navigator.share === "function"
                          ? t(A.share.label)
                          : t(copyToClip.label)
                      }
                      onClick={() => void exec.share()}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Recurrence sheet — rendered outside Drawer so it persists after drawer closes */}
      {task && showRecurrenceSheet && (
        <RecurrenceSheet
          open={showRecurrenceSheet}
          onOpenChange={setShowRecurrenceSheet}
          task={task}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Public export
// ---------------------------------------------------------------------------

export const MoreMenu = memo(function MoreMenu(props: MoreMenuProps) {
  const { isDesktop } = useBreakpoint()

  if (isDesktop) {
    return <DesktopMoreMenu {...props} />
  }
  return <MobileMoreMenu {...props} />
})
